import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Calendar, ChevronDown, ChevronRight, Download, FileSpreadsheet, Landmark,
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, XCircle, IndianRupee,
  FileText, Lock, BadgeCheck, Banknote, Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const PayStatusBadge = ({ status }) => {
  const map = {
    Paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
    "Partially Paid": "bg-amber-100 text-amber-700 border-amber-200",
    Pending: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <Badge variant="outline" className={map[status] || map.Pending} data-testid="payment-status-badge">{status}</Badge>;
};

export default function MonthlyInvoices() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({ month: "all", year: "all", batch: "all", resource: "", status: "all", task: "" });

  // BLKPAY dialog
  const [blkGroup, setBlkGroup] = useState(null);
  const [blkValidation, setBlkValidation] = useState(null);
  const [blkDate, setBlkDate] = useState("");
  const [blkRemarks, setBlkRemarks] = useState("");
  const [blkLoading, setBlkLoading] = useState(false);
  const [blkExporting, setBlkExporting] = useState(false);

  // Missing bank dialog
  const [missingGroup, setMissingGroup] = useState(null);

  // Mark paid dialog
  const [payGroup, setPayGroup] = useState(null);
  const [payForm, setPayForm] = useState({ payment_status: "Paid", transaction_reference: "", payment_date: "", mark_invoices_paid: true });
  const [paySaving, setPaySaving] = useState(false);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/monthly`);
      setGroups(res.data.groups || []);
      if ((res.data.groups || []).length > 0) {
        setExpanded((prev) => Object.keys(prev).length ? prev : { [res.data.groups[0].batch_key]: true });
      }
    } catch (e) {
      toast.error("Failed to load monthly invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const { months, years, batchNames, taskTypes } = useMemo(() => {
    const m = new Set(), y = new Set(), b = new Set(), t = new Set();
    groups.forEach((g) => {
      if (g.month?.includes("-")) { y.add(g.month.split("-")[0]); m.add(g.month.split("-")[1]); }
      b.add(g.batch_name);
      g.invoices.forEach((i) => i.task && t.add(i.task));
    });
    return { months: [...m].sort(), years: [...y].sort().reverse(), batchNames: [...b].sort(), taskTypes: [...t].sort() };
  }, [groups]);

  const filtered = useMemo(() => groups.map((g) => {
    if (filters.month !== "all" && g.month.split("-")[1] !== filters.month) return null;
    if (filters.year !== "all" && g.month.split("-")[0] !== filters.year) return null;
    if (filters.batch !== "all" && g.batch_name !== filters.batch) return null;
    let invoices = g.invoices;
    if (filters.resource) {
      const q = filters.resource.toLowerCase();
      invoices = invoices.filter((i) => (i.resource_display_name || "").toLowerCase().includes(q) || (i.resource_name || "").toLowerCase().includes(q));
    }
    if (filters.status !== "all") invoices = invoices.filter((i) => i.status === filters.status);
    if (filters.task) invoices = invoices.filter((i) => (i.task || "").toLowerCase().includes(filters.task.toLowerCase()));
    if (invoices.length === 0) return null;
    return { ...g, invoices };
  }).filter(Boolean), [groups, filters]);

  const totals = useMemo(() => ({
    invoices: filtered.reduce((s, g) => s + g.invoices.length, 0),
    net: filtered.reduce((s, g) => s + g.invoices.reduce((a, i) => a + (i.grand_total || 0), 0), 0),
    missing: filtered.reduce((s, g) => s + g.invoices.filter((i) => !i.bank_complete).length, 0),
  }), [filtered]);

  const exportRegister = async (g) => {
    try {
      const res = await axios.get(`${API}/invoices/monthly/${g.batch_key}/register`, { responseType: "blob" });
      downloadBlob(res.data, `Invoice_Register_${g.month_label.replace(" ", "_")}.xlsx`);
      toast.success("Invoice Register downloaded");
    } catch (e) { toast.error("Register export failed"); }
  };

  const openBlkpay = async (g) => {
    setBlkGroup(g);
    setBlkValidation(null);
    setBlkLoading(true);
    try {
      const res = await axios.post(`${API}/invoices/monthly/${g.batch_key}/blkpay/validate`, {});
      setBlkValidation(res.data);
      setBlkDate(res.data.transaction_date);
      setBlkRemarks(res.data.remarks);
    } catch (e) { toast.error("Validation failed"); }
    finally { setBlkLoading(false); }
  };

  const revalidate = async (date, remarks) => {
    if (!blkGroup) return;
    setBlkLoading(true);
    try {
      const res = await axios.post(`${API}/invoices/monthly/${blkGroup.batch_key}/blkpay/validate`, { transaction_date: date, remarks });
      setBlkValidation(res.data);
    } catch (e) { toast.error("Validation failed"); }
    finally { setBlkLoading(false); }
  };

  const exportBlkpay = async (anyway) => {
    setBlkExporting(true);
    try {
      const res = await axios.post(`${API}/invoices/monthly/${blkGroup.batch_key}/blkpay/export`,
        { transaction_date: blkDate, remarks: blkRemarks, export_anyway: anyway },
        { responseType: "blob" });
      const today = new Date();
      const fname = `BLKPAY_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}.xlsx`;
      downloadBlob(res.data, fname);
      toast.success(`${fname} downloaded — ready for SBI Net Banking upload`);
      setBlkGroup(null);
    } catch (e) {
      let msg = "BLKPAY export failed";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch {}
      }
      toast.error(msg);
    } finally { setBlkExporting(false); }
  };

  const savePaymentStatus = async () => {
    setPaySaving(true);
    try {
      await axios.put(`${API}/invoices/monthly/${payGroup.batch_key}/payment-status`, payForm);
      toast.success(`${payGroup.month_label} marked as ${payForm.payment_status}`);
      setPayGroup(null);
      fetchGroups();
    } catch (e) { toast.error("Failed to update payment status"); }
    finally { setPaySaving(false); }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="monthly-invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Monthly Payments</h1>
          <p className="text-sm text-slate-500 mt-0.5">Invoices grouped by locked batch — register & BLKPAY bank exports</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchGroups} data-testid="refresh-monthly-btn">
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Calendar, bg: "bg-indigo-100", fg: "text-indigo-600", label: "Months / Batches", value: filtered.length },
          { icon: FileText, bg: "bg-sky-100", fg: "text-sky-600", label: "Invoices", value: totals.invoices },
          { icon: IndianRupee, bg: "bg-emerald-100", fg: "text-emerald-600", label: "Net Payable", value: inr(totals.net) },
          { icon: AlertTriangle, bg: "bg-amber-100", fg: "text-amber-600", label: "Missing Bank Details", value: totals.missing },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border p-4" data-testid={`monthly-stat-${i}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.fg}`} />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="monthly-filters">
        <div className="w-32">
          <Label className="text-xs text-slate-500">Month</Label>
          <Select value={filters.month} onValueChange={(v) => setFilters((f) => ({ ...f, month: v }))}>
            <SelectTrigger data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{new Date(2000, Number(m) - 1).toLocaleString("en", { month: "long" })}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-28">
          <Label className="text-xs text-slate-500">Year</Label>
          <Select value={filters.year} onValueChange={(v) => setFilters((f) => ({ ...f, year: v }))}>
            <SelectTrigger data-testid="filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-xs text-slate-500">Locked Batch</Label>
          <Select value={filters.batch} onValueChange={(v) => setFilters((f) => ({ ...f, batch: v }))}>
            <SelectTrigger data-testid="filter-batch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Batches</SelectItem>
              {batchNames.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Label className="text-xs text-slate-500">Resource</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-slate-400" />
            <Input className="pl-8" placeholder="Search resource…" value={filters.resource}
              onChange={(e) => setFilters((f) => ({ ...f, resource: e.target.value }))} data-testid="filter-resource" />
          </div>
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">Invoice Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {["generated", "sent", "paid", "unpaid", "overdue"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-xs text-slate-500">Task Type</Label>
          <Input placeholder="e.g. Translation" value={filters.task}
            onChange={(e) => setFilters((f) => ({ ...f, task: e.target.value }))} data-testid="filter-task" list="task-type-options" />
          <datalist id="task-type-options">{taskTypes.map((t) => <option key={t} value={t} />)}</datalist>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-slate-500" data-testid="monthly-empty">
          <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          No invoices match the current filters.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((g) => {
            const isOpen = !!expanded[g.batch_key];
            return (
              <div key={g.batch_key} className="bg-white rounded-xl border overflow-hidden" data-testid={`month-card-${g.batch_key}`}>
                {/* Card header */}
                <button className="w-full text-left px-4 sm:px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded((p) => ({ ...p, [g.batch_key]: !isOpen }))}
                  data-testid={`month-card-toggle-${g.batch_key}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />}
                    <div className="min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{g.month_label}</span>
                        {g.batch_status === "locked" && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                      </div>
                      <p className="text-xs text-slate-500">{g.batch_name}
                        {g.locked_at && <> · Locked {String(g.locked_at).slice(0, 10)}</>}
                        {g.generated_date && <> · Generated {String(g.generated_date).slice(0, 10)}</>}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 ml-auto text-right">
                      <div><p className="text-xs text-slate-400">Invoices</p><p className="font-semibold text-slate-800">{g.invoices.length}</p></div>
                      <div><p className="text-xs text-slate-400">Gross</p><p className="font-semibold text-slate-800">{inr(g.invoices.reduce((s, i) => s + i.subtotal, 0))}</p></div>
                      <div><p className="text-xs text-slate-400">TDS</p><p className="font-semibold text-rose-600">-{inr(g.invoices.reduce((s, i) => s + i.tds_amount, 0))}</p></div>
                      <div><p className="text-xs text-slate-400">Net Payable</p><p className="font-bold text-emerald-700">{inr(g.invoices.reduce((s, i) => s + i.grand_total, 0))}</p></div>
                      <PayStatusBadge status={g.payment_status} />
                      {g.bank_missing_count > 0 && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />{g.bank_missing_count} no bank
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t">
                    {/* Actions */}
                    <div className="px-4 sm:px-5 py-3 bg-slate-50 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => exportRegister(g)} data-testid={`export-register-${g.batch_key}`}>
                        <FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-600" />Invoice Register
                      </Button>
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => openBlkpay(g)} data-testid={`export-blkpay-${g.batch_key}`}>
                        <Landmark className="w-4 h-4 mr-1" />Export BLKPAY
                      </Button>
                      {g.bank_missing_count > 0 && (
                        <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => setMissingGroup(g)} data-testid={`missing-bank-${g.batch_key}`}>
                          <AlertTriangle className="w-4 h-4 mr-1" />Missing Bank Details ({g.bank_missing_count})
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300"
                        onClick={() => { setPayGroup(g); setPayForm({ payment_status: "Paid", transaction_reference: g.transaction_reference || "", payment_date: g.payment_date || new Date().toISOString().slice(0, 10), mark_invoices_paid: true }); }}
                        data-testid={`mark-paid-${g.batch_key}`}>
                        <BadgeCheck className="w-4 h-4 mr-1" />Payment Status
                      </Button>
                    </div>

                    {/* Invoice table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Resource</TableHead>
                            <TableHead>Task</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">TDS</TableHead>
                            <TableHead className="text-right">Net</TableHead>
                            <TableHead>Bank</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {g.invoices.map((inv) => (
                            <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                              <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                              <TableCell className="text-xs text-slate-500">{String(inv.invoice_date || "").slice(0, 10)}</TableCell>
                              <TableCell className="font-medium">{inv.resource_display_name}</TableCell>
                              <TableCell className="text-xs">{inv.task}</TableCell>
                              <TableCell className="text-right">{inr(inv.subtotal)}</TableCell>
                              <TableCell className="text-right text-rose-600">-{inr(inv.tds_amount)} <span className="text-slate-400">({inv.tds_percentage}%)</span></TableCell>
                              <TableCell className="text-right font-semibold">{inr(inv.grand_total)}</TableCell>
                              <TableCell>
                                {inv.bank_complete
                                  ? <span className="text-xs text-slate-600 inline-flex items-center gap-1"><Banknote className="w-3.5 h-3.5 text-emerald-500" />{inv.bank_summary}</span>
                                  : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">No bank</Badge>}
                              </TableCell>
                              <TableCell><Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge></TableCell>
                              <TableCell>
                                <Button size="sm" variant="ghost" title="Download PDF"
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { responseType: "blob" });
                                      downloadBlob(res.data, `invoice_${(inv.invoice_number || inv.id).replace(/[/ ]/g, "_")}.pdf`);
                                    } catch { toast.error("PDF download failed"); }
                                  }}
                                  data-testid={`download-pdf-${inv.id}`}>
                                  <Download className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── BLKPAY Dialog ── */}
      <Dialog open={!!blkGroup} onOpenChange={(o) => !o && setBlkGroup(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blkpay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Landmark className="w-5 h-5" />BLKPAY Export — {blkGroup?.month_label}</DialogTitle>
            <DialogDescription>Bank-ready Excel for SBI Net Banking upload. Validation runs before every export.</DialogDescription>
          </DialogHeader>

          {blkLoading && !blkValidation ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : blkValidation && (
            <div className="space-y-4">
              {/* Validation summary */}
              <div className="flex flex-wrap gap-2" data-testid="blkpay-validation-summary">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{blkValidation.ready_count} Payments Ready · {inr(blkValidation.total_amount)}</Badge>
                {blkValidation.blocked_count > 0 && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><XCircle className="w-3.5 h-3.5 mr-1" />{blkValidation.blocked_count} Blocked</Badge>}
                {blkValidation.warning_count > 0 && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><AlertTriangle className="w-3.5 h-3.5 mr-1" />{blkValidation.warning_count} Warnings</Badge>}
              </div>

              {blkValidation.global_errors.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700 space-y-1" data-testid="blkpay-global-errors">
                  {blkValidation.global_errors.map((e, i) => <p key={i} className="flex items-start gap-1.5"><XCircle className="w-4 h-4 mt-0.5 shrink-0" />{e}</p>)}
                </div>
              )}

              {/* Editable date + remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Transaction Date (DD/MM/YYYY — today or future)</Label>
                  <Input value={blkDate} onChange={(e) => setBlkDate(e.target.value)} onBlur={() => revalidate(blkDate, blkRemarks)} data-testid="blkpay-date-input" />
                </div>
                <div>
                  <Label className="text-xs">Remarks <span className={blkRemarks.length > 35 ? "text-rose-600 font-semibold" : "text-slate-400"}>({blkRemarks.length}/35)</span></Label>
                  <Input value={blkRemarks} maxLength={60} onChange={(e) => setBlkRemarks(e.target.value)} onBlur={() => revalidate(blkDate, blkRemarks)} data-testid="blkpay-remarks-input" />
                </div>
              </div>
              <p className="text-xs text-slate-500">Debit Account: <span className="font-mono font-medium">{blkValidation.debit_account_number || "— not set —"}</span> · Type: {blkValidation.transaction_type} · Currency: INR</p>

              {/* Row details */}
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Resource</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>IFSC</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Checks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blkValidation.rows.map((r) => (
                      <TableRow key={r.invoice_id} className={r.errors.length ? "bg-rose-50/50" : ""}>
                        <TableCell className="font-medium text-sm">{r.resource}</TableCell>
                        <TableCell className="font-mono text-xs">{r.account_number || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{r.ifsc || "—"}</TableCell>
                        <TableCell className="text-right">{inr(r.amount)}</TableCell>
                        <TableCell>
                          {r.errors.length === 0 && r.warnings.length === 0 && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                          {r.errors.map((e, i) => <p key={i} className="text-xs text-rose-600 flex items-center gap-1"><XCircle className="w-3 h-3" />{e}</p>)}
                          {r.warnings.map((w, i) => <p key={i} className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{w}</p>)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setBlkGroup(null)} data-testid="blkpay-cancel-btn">Cancel</Button>
                {blkValidation.blocked_count > 0 && (
                  <Button variant="outline" className="text-amber-700 border-amber-300" disabled={!blkValidation.can_export_anyway || blkExporting}
                    onClick={() => exportBlkpay(true)} data-testid="blkpay-export-anyway-btn">
                    {blkExporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                    Export Anyway ({blkValidation.ready_count} valid)
                  </Button>
                )}
                <Button className="bg-slate-900 hover:bg-slate-800" disabled={!blkValidation.can_export || blkExporting}
                  onClick={() => exportBlkpay(false)} data-testid="blkpay-export-btn">
                  {blkExporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  Export BLKPAY
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Missing Bank Details Dialog ── */}
      <Dialog open={!!missingGroup} onOpenChange={(o) => !o && setMissingGroup(null)}>
        <DialogContent className="max-w-lg" data-testid="missing-bank-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Missing Bank Details — {missingGroup?.month_label}</DialogTitle>
            <DialogDescription>These resources have no complete bank account on file. Add them in Bank Accounts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {missingGroup?.invoices.filter((i) => !i.bank_complete).map((i) => (
              <div key={i.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                <div>
                  <p className="font-medium text-sm">{i.resource_display_name}</p>
                  <p className="text-xs text-slate-500">{i.resource_name} · {i.invoice_number}</p>
                </div>
                <span className="font-semibold text-sm">{inr(i.grand_total)}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingGroup(null)}>Close</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => { window.location.href = "/bank-accounts"; }} data-testid="goto-bank-accounts-btn">
              <Landmark className="w-4 h-4 mr-1" />Open Bank Accounts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Payment Status Dialog ── */}
      <Dialog open={!!payGroup} onOpenChange={(o) => !o && setPayGroup(null)}>
        <DialogContent className="max-w-md" data-testid="payment-status-dialog">
          <DialogHeader>
            <DialogTitle>Payment Status — {payGroup?.month_label}</DialogTitle>
            <DialogDescription>Record the bulk payment for this month's batch.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={payForm.payment_status} onValueChange={(v) => setPayForm((f) => ({ ...f, payment_status: v }))}>
                <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Paid", "Partially Paid", "Pending"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Transaction Reference (UTR / batch ref)</Label>
              <Input value={payForm.transaction_reference} onChange={(e) => setPayForm((f) => ({ ...f, transaction_reference: e.target.value }))} data-testid="payment-txn-ref-input" />
            </div>
            <div>
              <Label className="text-xs">Payment Date</Label>
              <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm((f) => ({ ...f, payment_date: e.target.value }))} data-testid="payment-date-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={payForm.mark_invoices_paid} onCheckedChange={(v) => setPayForm((f) => ({ ...f, mark_invoices_paid: !!v }))} data-testid="mark-invoices-paid-checkbox" />
              Also mark all {payGroup?.invoices.length} invoices as <b>paid</b>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayGroup(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={paySaving} onClick={savePaymentStatus} data-testid="save-payment-status-btn">
              {paySaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-1" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
